import React, { useContext, useEffect, useRef, useState, memo } from "react";
import {
	Box,
	Paper,
	Typography,
	Button,
	Divider,
	Stack,
	Chip,
} from "@mui/material";
import reactHtmlParser from "react-html-parser";
import { useIntl } from "react-intl";
import { useWindowResize } from "../../../../hooks/useWindowResize";
import { ServiceDescription } from "../../../../types/entities";
import {
	MultistepContext,
	MultistepContextValues,
} from "../../../../types/context";
import { theme } from "../../../../styles/themeProvider";
import "./carouselStyle.css";

interface CarouselPaperProps {
	service: ServiceDescription;
	index: number;
	activeStep: number;
	onChooseService: (service: ServiceDescription) => void;
	expanded: boolean;
	onToggleExpand: () => void;
}

const CarouselPaper: React.FC<CarouselPaperProps> = ({
	service,
	index,
	activeStep,
	onChooseService,
	expanded,
	onToggleExpand,
}) => {
	const { formatMessage } = useIntl();
	const { reservation } = useContext(
		MultistepContext
	) as MultistepContextValues;

	const { isXSmall } = useWindowResize();

	const handleClickSeeMore = () => {
		onToggleExpand();
	};

	return (
		<Stack
			component="div"
			key={index}
			direction={isXSmall ? "column" : "row"}
			sx={[
				styles.border,
				styles.container,
				{
					border:
						reservation.service && reservation.service.id === service.id
							? `2px solid ${theme.palette.primary.main}`
							: "",
				},
			]}
		>
			{isXSmall && (
				<ServiceImage
					activeStep={activeStep}
					index={index}
					service={service}
					isXSmall={isXSmall}
				/>
			)}
			<Paper
				square
				elevation={0}
				sx={{
					...styles.paper,
					...styles.border,
					...(isXSmall && { maxHeight: "auto" }),
				}}
			>
				<Stack sx={styles.textContainer}>
					<Typography
						sx={[styles.title, { display: "inline-block" }]}
						component="div"
					>
						<Chip
							variant="filled"
							color="success"
							label={`$${service.price}`}
							sx={{
								float: "right", // Permite que el texto fluya alrededor del Chip
								width: "fit-content",
								padding: "6px 2px",
								marginLeft: 1,
							}}
						/>
						{service.name}
					</Typography>
					<div>
						<Typography component="span" sx={[styles.description, styles.text]}>
							{reactHtmlParser(service.shortDescription)}
						</Typography>
						<Divider />
						{isXSmall && expanded && (
							<Typography component="span" sx={[styles.text, styles.body]}>
								{reactHtmlParser(service.description)}
							</Typography>
						)}
					</div>
					<Typography
						component="span"
						onClick={handleClickSeeMore}
						sx={{
							...styles.text,
							...styles.body,
							alignSelf: "flex-end",
							justifyContent: "flex-end",
							px: 3,
							pt: 1,
							"&:hover": { cursor: "pointer" },
						}}
					>
						{expanded
							? formatMessage({
									id: "service.seeLess",
									defaultMessage: "Ver menos...",
							  })
							: formatMessage({
									id: "service.seeMore",
									defaultMessage: "Ver más...",
							  })}
					</Typography>
				</Stack>

				<Button
					variant="contained"
					sx={styles.selectButton}
					onClick={() => onChooseService(service)}
				>
					{formatMessage({
						id: "service.choose",
						defaultMessage: "Seleccionar",
					})}
				</Button>
			</Paper>
			{!isXSmall && (
				<ServiceImage
					activeStep={activeStep}
					index={index}
					service={service}
					isXSmall={isXSmall}
					showContent={expanded}
					content={service.description}
				/>
			)}
		</Stack>
	);
};

export default memo(CarouselPaper);

const ServiceImage: React.FC<{
	activeStep: number;
	index: number;
	service: ServiceDescription;
	isXSmall: boolean;
	showContent?: boolean;
	content?: string;
}> = ({ activeStep, index, service, isXSmall, showContent, content }) => {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (showContent) {
			setVisible(true);
			setTimeout(() => setVisible(false), 0); // Delay of 0 to trigger re-render and hide text instantly
		}
	}, [showContent]);

	return (
		<>
			{Math.abs(activeStep - index) <= 2 && (
				<Box
					component="div"
					sx={
						isXSmall
							? [styles.border, styles.imageMobile]
							: [styles.border, styles.image]
					}
				>
					<Box
						component="img"
						src={`data:image/png;base64,${service.image}`}
						alt={service.name}
						sx={styles.imageContent}
					/>
					{showContent && content && (
						<Stack sx={styles.overlayContent}>
							<Stack
								className="serviceText"
								sx={{
									...styles.textStack,
									visibility: visible ? "hidden" : "visible",
									opacity: visible ? 0 : 1,
								}}
							>
								<Typography
									component="span"
									sx={[styles.body, styles.text, { color: "white" }]}
								>
									{reactHtmlParser(content)}
								</Typography>
							</Stack>
						</Stack>
					)}
				</Box>
			)}
		</>
	);
};

export const styles = {
	border: {
		borderRadius: "7px",
	},
	container: {
		border: `2px solid ${theme.palette.secondary.main}`,
		backgroundColor: theme.palette.secondary.light,
		overflow: "hidden",
	},
	image: {
		position: "relative",
		height: "264px",
		width: "62%",
		objectFit: "cover",
		borderTopLeftRadius: "0px",
		borderBottomLeftRadius: "0px",
	},
	imageMobile: {
		width: "auto",
		height: "240px",
		objectFit: "cover",
		borderTopRightRadius: "0px",
		borderBottomRightRadius: "0px",
	},
	imageContent: {
		width: "100%",
		height: "100%",
		objectFit: "cover",
		objectPosition: "center", // Para centrar la imagen dentro del contenedor
	},
	paper: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		justifyContent: "space-between",
		maxHeight: "264px",
		p: 2,
		backgroundColor: theme.palette.background.paper,
	},
	textContainer: {
		display: "flex",
		flex: 1,
		justifyContent: "space-between",
		flexDirection: "column",
		position: "relative",
	},
	text: {
		color: "#000",
		lineHeight: { mobile: "22px", sm: "18px" },
	},
	title: {
		fontSize: "16px",
		fontWeight: 600,
		color: "#000",
	},
	description: {
		fontSize: { mobile: "16px", sm: "14px" },
		fontWeight: 300,
	},
	body: {
		fontWeight: 300,
		fontStyle: "italic",
		fontSize: { mobile: "15px", sm: "13px" },
	},
	selectButton: {
		mt: 1,
		height: "32px",
		fontWeight: 400,
	},
	overlayContent: {
		position: "absolute",
		top: 0,
		left: 0,
		backgroundColor: "rgba(0, 0, 0, 0.7)",
		height: "100%",
		width: "100%",
		borderTopRightRadius: "7px",
		borderBottomRightRadius: "7px",
		padding: "1rem 0.5rem 1rem 2rem",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	textStack: {
		overflowY: "auto",
		pr: 2,
		transition: "visibility 0s, opacity 0.3s linear",
	},
};
